import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";

// ----------------------------------------------------------------------
// Estado del wizard de descubrimiento de impresoras (Fase 3B).
// El TPV lanza un scan y cada worker responde con sus hallazgos. Si hay
// varios workers (tablet + PC de cocina, por ejemplo), se agregan los
// resultados por IP, quedándonos con el mejor ConnectMs.
// ----------------------------------------------------------------------

const initialState = {
  isScanning: false,
  /** IPs deduplicadas; shape: { ip, port, connectMs, workerInstanceId, workerHostname } */
  results: [],
  /** Último lote recibido — shape: { workerInstanceId, workerHostname, subnet, startedAt, finishedAt, found } */
  lastBatch: null,
  hasError: false,
  errorMessage: null,
};

const slice = createSlice({
  name: "printerDiscovery",
  initialState,
  reducers: {
    startScan(state) {
      state.isScanning = true;
      state.hasError = false;
      state.errorMessage = null;
      // No limpiamos `results` — durante un rescan incremental, sigue habiendo data visible.
    },
    scanError(state, action) {
      state.isScanning = false;
      state.hasError = true;
      state.errorMessage = action.payload;
    },
    /**
     * Llega desde useHubWorkerStatus cuando el hub emite discovery/results/{id}.
     * Merge por IP: para cada IP encontrada, nos quedamos con el mejor connectMs.
     */
    receiveResults(state, action) {
      const batch = action.payload;
      if (!batch) return;
      state.isScanning = false;
      state.lastBatch = batch;

      const byIp = new Map(state.results.map((r) => [r.ip, r]));
      (batch.found || []).forEach((p) => {
        const existing = byIp.get(p.ip);
        if (!existing || p.connectMs < existing.connectMs) {
          byIp.set(p.ip, {
            ip: p.ip,
            port: p.port,
            connectMs: p.connectMs,
            banner: p.banner,
            workerInstanceId: batch.workerInstanceId,
            workerHostname: batch.workerHostname,
          });
        }
      });
      state.results = Array.from(byIp.values()).sort((a, b) => a.connectMs - b.connectMs);
    },
    reset(state) {
      state.isScanning = false;
      state.results = [];
      state.lastBatch = null;
      state.hasError = false;
      state.errorMessage = null;
    },
  },
});

export default slice.reducer;
export const { receiveResults, reset } = slice.actions;

export function startDiscoveryScan(subnet = null, port = 9100) {
  return async (dispatch) => {
    dispatch(slice.actions.startScan());
    try {
      await axios.post("/printerdiscovery/start", { subnet, port });
      // No marcamos isScanning=false aquí — lo hace receiveResults cuando llegue
      // el push. Si no llega nada en ~20s, el usuario puede re-lanzar; podríamos
      // añadir un timeout pero mejor UX sin mostrar error prematuro.
    } catch (error) {
      dispatch(slice.actions.scanError(error?.message || "Error lanzando scan"));
    }
  };
}
